import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  nextStatusOptions,
  useChangeBookingStatus,
  type BookingStatus,
  type StatusOption,
} from "@/lib/booking-status";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./StatusBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { useToast } from "./Toast";

/**
 * Booking-side-panel header status badge with a popover menu of valid
 * next states. Click the badge → Radix DropdownMenu of "Mark Confirmed
 * / Mark Completed / …" → small confirm dialog → PATCH `/bookings/{id}`.
 *
 * Cancel routes through the existing footer flow (separate destructive
 * affordance), so this menu intentionally omits Cancel.
 *
 * Backed by `@radix-ui/react-dropdown-menu` — focus trap, click-outside,
 * Escape, Arrow Up/Down nav, restore-focus-on-close all handled.
 */
export function BookingStatusMenu({
  bookingId,
  currentStatus,
}: {
  bookingId: string;
  currentStatus: string;
}) {
  const [pending, setPending] = useState<StatusOption | null>(null);
  const toast = useToast();

  const change = useChangeBookingStatus(bookingId);
  const options = nextStatusOptions(currentStatus as BookingStatus);

  const onConfirm = () => {
    if (!pending) return;
    change.mutate(pending.next, {
      onSuccess: () => {
        toast.success(
          "Status updated",
          `Now ${pending.label.replace(/^Mark |^Reopen as /, "")}.`,
        );
        setPending(null);
      },
      onError: (e) => {
        toast.error("Couldn't update status", (e as Error).message);
        setPending(null);
      },
    });
  };

  // No transitions available → render the badge as a static chip.
  if (options.length === 0) {
    return <StatusBadge label={currentStatus} />;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 rounded-full hover:ring-2 hover:ring-primary/30 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="Change booking status"
          >
            <StatusBadge label={currentStatus} />
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground -ml-1 mr-0.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Change status
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((opt) => (
            <DropdownMenuItem
              key={opt.next}
              onSelect={() => setPending(opt)}
              className={cn(
                opt.tone === "warning" && "text-status-pending",
                opt.tone === "danger" && "text-destructive",
              )}
            >
              {opt.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={onConfirm}
        title={pending?.label ?? ""}
        description={pending?.description ?? ""}
        confirmLabel={change.isPending ? "Saving…" : "Confirm"}
        pending={change.isPending}
      />
    </>
  );
}
